/**
 * Bundled ecosystem templates (under runtime resources/ecosystems/).
 */

export type EcosystemId = 'ui-only' | 'vscode' | 'zephyr' | 'linux';

export type UiProjectTemplate = 'empty' | 'basic';

export type ProjectLvglVersion = '9.4.0' | '9.5.0';

export type ProjectTheme = 'default' | 'simple';

export const DEFAULT_PROJECT_LVGL_VERSION: ProjectLvglVersion = '9.5.0';

export const toResourceLvglVersion = (version: ProjectLvglVersion): string => `v${version}`;

export type UiProjectStartingOptionId = 'basic' | 'empty-simple' | 'empty-default';

export type UiProjectStartingOption = {
    id: UiProjectStartingOptionId;
    label: string;
    description: string;
    uiTemplate: UiProjectTemplate;
    figmaReady: boolean;
    theme: ProjectTheme;
};

export const UI_PROJECT_STARTING_OPTIONS: UiProjectStartingOption[] = [
    {
        id: 'basic',
        label: 'Basic template with a useful set of components',
        description: 'Get started easily',
        uiTemplate: 'basic',
        figmaReady: false,
        theme: 'simple'
    },
    {
        id: 'empty-simple',
        label: 'Empty template with a simplified theme',
        description: 'Start from scratch, design freely or import Figma projects',
        uiTemplate: 'empty',
        figmaReady: true,
        theme: 'simple'
    },
    {
        id: 'empty-default',
        label: 'Empty template with the default LVGL theme',
        description: 'Create your own UI from scratch',
        uiTemplate: 'empty',
        figmaReady: false,
        theme: 'default'
    }
];

export const DEFAULT_UI_PROJECT_STARTING_OPTION: UiProjectStartingOption = UI_PROJECT_STARTING_OPTIONS[0];

export const DEFAULT_PROJECT_DISPLAY = {
    width: 320,
    height: 240
} as const;

export type EcosystemDefinition = {
    id: EcosystemId;
    label: string;
    icon: string;
    iconSize: number;
    description: string;
    /** When false, the LVGL XML project is created at the destination root instead of ui/. */
    usesUiSubfolder: boolean;
    defaultFolderName: string;
};

export const ECOSYSTEMS: EcosystemDefinition[] = [
    {
        id: 'ui-only',
        label: 'UI only',
        icon: 'lvgl-ui',
        iconSize: 30,
        description: 'LVGL XML project without a target platform wrapper.',
        usesUiSubfolder: false,
        defaultFolderName: 'lvgl_ui_project'
    },
    {
        id: 'vscode',
        label: 'VSCode',
        icon: 'vscode',
        iconSize: 30,
        description: 'PC simulator project for VSCode with CMake and SDL or Win32.',
        usesUiSubfolder: true,
        defaultFolderName: 'lvgl_vscode_project'
    },
    {
        id: 'zephyr',
        label: 'Zephyr',
        icon: 'zephyr',
        iconSize: 30,
        description: 'Embedded project targeting Zephyr RTOS.',
        usesUiSubfolder: true,
        defaultFolderName: 'lvgl_zephyr_project'
    },
    {
        id: 'linux',
        label: 'Linux',
        icon: 'linux',
        iconSize: 24,
        description: 'Embedded Linux framebuffer project with exported UI in ui/.',
        usesUiSubfolder: true,
        defaultFolderName: 'lvgl_linux_project'
    }
];

export const getEcosystem = (ecosystemId: EcosystemId): EcosystemDefinition => {
    const match: EcosystemDefinition | undefined = ECOSYSTEMS.find((ecosystem) => ecosystem.id === ecosystemId);
    return match ?? ECOSYSTEMS[0];
};

export const getEcosystemReadmePathSegment = (ecosystemId: EcosystemId): string =>
    `ecosystems/${ecosystemId}/template/README.md`;

export const getLvglEditorPathForCreatedProject = (projectPath: string, ecosystemId?: EcosystemId): string => {
    if (ecosystemId && getEcosystem(ecosystemId).usesUiSubfolder) {
        const separator: string = projectPath.includes('\\') ? '\\' : '/';
        return `${projectPath.replace(/[\\/]+$/, '')}${separator}ui`;
    }
    return projectPath;
};
